import { useQuery } from "convex/react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

export interface DeckExtraReviewMessageProps {
  deckId: Id<"decks">;
  cardCount: number;
}

export const DeckExtraReviewMessage = (props: DeckExtraReviewMessageProps) => {
  // To get animations right, we want to re-create the component each time we're showing a different deck.
  return <DeckExtraReviewMessageInternal key={props.deckId} {...props} />;
};

const DeckExtraReviewMessageInternal = ({ deckId, cardCount }: DeckExtraReviewMessageProps) => {
  const deckStats = useQuery(api.aggregates.reviewsCount.byDeck, { deckId });
  const lastReviewForgottenCardCount = useQuery(api.aggregates.cardCount.lastReviewForgotten, {
    deckId,
  });

  const cardsReviewed = deckStats?.totalReviews ?? 0;
  const daysWithReviews = deckStats?.daysWithReviews ?? 0;

  const cardString = cardCount > 1 ? "cards" : "the card";
  const reviewString = cardsReviewed > 1 ? "reviews" : "review";
  const cardsReviewedString = (
    <>
      <strong>
        {cardsReviewed} {reviewString}
      </strong>{" "}
      of {cardString}
    </>
  );
  const daysWithReviewsString =
    daysWithReviews > 1
      ? daysWithReviews > 14
        ? `${Math.floor(daysWithReviews / 7)} weeks`
        : `${daysWithReviews} days`
      : "1 day";

  const message = (
    <>
      You've done {cardsReviewedString} in this deck over <strong>{daysWithReviewsString}</strong>.
      You don't need to review anything right now, but you can do extra reviews if you want.
    </>
  );

  const reviewRecentlyMissedButton = (
    <Button variant="muted" disabled={!lastReviewForgottenCardCount}>
      REVIEW RECENTLY MISSED{" "}
      <span
        className={`transition-opacity duration-500 ${deckStats ? "opacity-100" : "opacity-0"}`}
      >
        ({lastReviewForgottenCardCount ?? 0})
      </span>
    </Button>
  );

  return (
    <Card className="mb-6 bg-muted border-muted/50">
      <CardContent>
        <h2 className="text-xl font-semibold mb-2">Great work!</h2>
        <p className="mb-4">
          <span
            className={`transition-opacity duration-500 ${deckStats ? "opacity-100" : "opacity-0"}`}
          >
            {message}
          </span>
        </p>
        <div className="flex flex-col sm:flex-row justify-start gap-2 sm:items-end">
          {lastReviewForgottenCardCount ? (
            <Link to={`/review-missed/${deckId}`}>{reviewRecentlyMissedButton}</Link>
          ) : (
            <div>{reviewRecentlyMissedButton}</div>
          )}
          <Link to={`/review-all/${deckId}`}>
            <Button variant="outline">REVIEW ALL</Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
